/**
 * resolveApiUrl — the single rule for turning configuration into an API base URL.
 *
 * Resolution order (unchanged): runtime `APP_CONFIG` (written by docker-entrypoint.sh at
 * container startup) → build-time `VITE_*` → this function's last step.
 *
 * That last step is where the safety lives (owner rule 2026-08-13, 12-factor "config"):
 *   • DEV build  → fall back to the DEV environment. Local convenience: a fresh clone or a
 *     git worktree has no `.env`/`.env.local` (both gitignored), and `npm run dev` should
 *     still work without pointing anyone at a real environment by accident.
 *   • PROD build → **throw**. A production bundle with nothing configured is a broken
 *     deployment, and it must fail loudly instead of silently talking to whichever
 *     environment a hardcoded default happened to name. A wrong-environment SPA that looks
 *     healthy is far more dangerous than one that refuses to start.
 *
 * Deployed environments are unaffected: the entrypoint always writes `config.js`, so the
 * throw is unreachable unless that step genuinely failed.
 */
export function resolveApiUrl(configured: string | undefined | null, devDefault: string, varName: string): string {
  if (configured && configured.trim().length > 0) return configured;
  if (import.meta.env.DEV) return devDefault;
  // console.error first: a throw at module load leaves a blank page, and the console line is
  // what tells an operator WHICH variable is missing.
  const message =
    `[config] ${varName} is not configured. Set it in APP_CONFIG (written at container startup) ` +
    `or as ${varName} at build time. Refusing to guess an environment.`;
  console.error(message);
  throw new Error(message);
}
